import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { isStrongPassword } from "validator";
import Button from "../ui/button";
import { EditUser, UserRole } from "../../types/user";
import Alert from "../alert/Alert";
import { useActivateUser, useAdminResetUserPassword } from "../../hooks/useUsers";
import useAuth from "../../hooks/useAuth";
import useSites from "../../hooks/useSites";
import { isSdiAdmin } from "../../utils/roles";
import { PASS_VALIDATORS } from "../../validators/validators";

type ManageUserProps = {
  user: EditUser | null;
  error?: Error | null;
  success?: {message: string} | null;
  onSave: (action: "ADD" | "UPDATE", user: EditUser) => void;
  onDelete: () => void;
}

export default function ManageUser ({user, error, success, onSave, onDelete}: ManageUserProps) {
  const intl = useIntl();
  const { user: currentUser, isAdmin } = useAuth();
  const { sites, fetchSites } = useSites();

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [selectedRole, setSelectedRole] = useState<UserRole>(user?.role || UserRole.operator);
  const [selectedSiteIds, setSelectedSiteIds] = useState<number[]>(user?.siteIds ?? []);
  const formRef = useRef<HTMLFormElement>(null);
  const firstNameRef = useRef<HTMLInputElement>(null);
  const lastNameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const passRef = useRef<HTMLInputElement>(null);

  const {resetUserPassword, response: resetRes, isLoading: resetLoading, error: resetError} = useAdminResetUserPassword();
  const {activateUser, response: activateRes, isLoading: activateLoading, error: activateError} = useActivateUser();

  useEffect(() => {
    fetchSites();
  }, [fetchSites]);

  useEffect(() => {
    setSelectedRole(user?.role || UserRole.operator);
    setSelectedSiteIds(user?.siteIds ?? []);
    setPasswordError(false);
    if (passRef.current) {
      passRef.current.value = "";
    }
  }, [user]);

  useEffect(() => {
    formRef.current?.addEventListener('change', () => {
      if (!isSubmitted) {
        setIsSubmitted(false);
      }
      setPasswordError(false);
    })
    return formRef.current?.removeEventListener('change', () => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const toggleSite = (siteId: number) => {
    setSelectedSiteIds((current) =>
      current.includes(siteId)
        ? current.filter((id) => id !== siteId)
        : [...current, siteId]
    );
  };

  const getValidPassword = () => {
    const password = passRef.current?.value ?? "";
    if (!isStrongPassword(password, PASS_VALIDATORS)) {
      setPasswordError(true);
      return null;
    }
    setPasswordError(false);
    return password;
  };

  function saveUser () {
    setIsSubmitted(true);
    if (formRef.current?.checkValidity()) {
      if (selectedRole === UserRole.operator && selectedSiteIds.length === 0) {
        return;
      }

      const userToSave: EditUser = {
        id: user?.id,
        email: emailRef.current!.value,
        firstName: firstNameRef.current!.value,
        lastName: lastNameRef.current!.value,
        role: selectedRole,
        siteIds: selectedRole === UserRole.operator ? selectedSiteIds : [],
      }

      if (!user) {
        const password = getValidPassword();
        if (!password) {
          return;
        }
        userToSave.password = password;
      }

      onSave(user ? 'UPDATE' : "ADD", userToSave);
    }
  }

  function handleResetPassword () {
    if (!user?.id) {
      return;
    }
    const password = getValidPassword();
    if (!password) {
      return;
    }
    resetUserPassword(user.id, password);
  }

  const canAssignSdiAdmin = isAdmin();
  const canAssignSiteAdmin = isSdiAdmin(currentUser?.role);

  return (
    <>
      <h3 className="mb-5 text-lg font-normal text-shadow border-b w-fit border-slate-300">
        {user
          ? intl.formatMessage({ id: "users.manage.editHeading" })
          : intl.formatMessage({ id: "users.manage.addHeading" })}
      </h3>
      <form ref={formRef}>
        <div className="mb-3">
          <label className="subtitle block" htmlFor="lastName">
            {intl.formatMessage({ id: "users.manage.lastName" })}
          </label>
          <input
            ref={lastNameRef}
            type="text"
            id="lastName"
            required={true}
            className="input mb-0 peer"
            autoComplete='off'
            placeholder={intl.formatMessage({ id: "users.profile.lastName" })}
            defaultValue={user?.lastName}
          />
          { isSubmitted &&
            <span className="mt-2 hidden peer-invalid:block text-xs text-red-500">
              {intl.formatMessage({ id: "users.manage.lastNameRequired" })}
            </span>
          }
        </div>

        <div className="mb-3">
          <label className="subtitle block" htmlFor="firstName">
            {intl.formatMessage({ id: "users.manage.firstName" })}
          </label>
          <input
            ref={firstNameRef}
            type="text"
            id="firstName"
            required={true}
            className="input mb-0 peer"
            autoComplete='off'
            placeholder={intl.formatMessage({ id: "users.profile.firstName" })}
            defaultValue={user?.firstName}
          />
          { isSubmitted &&
            <span className="mt-2 hidden peer-invalid:block text-xs text-red-500">
              {intl.formatMessage({ id: "users.manage.firstNameRequired" })}
            </span>
          }
        </div>

        <div className="mb-3">
          <label className="subtitle block" htmlFor="email">
            {intl.formatMessage({ id: "users.manage.email" })}
          </label>
          <input
            ref={emailRef}
            id="email"
            type="email"
            required={true}
            autoComplete="off"
            className="input mb-0 peer"
            placeholder={intl.formatMessage({ id: "auth.register.email" })}
            defaultValue={user?.email}
          />
          { isSubmitted &&
            <span className="mt-2 hidden peer-invalid:block text-xs text-red-500">
              {intl.formatMessage({ id: "users.manage.emailInvalid" })}
            </span>
          }
        </div>

        <div className="mb-3">
          <label className="subtitle block" htmlFor="password">
            {intl.formatMessage({ id: "users.manage.password" })}
          </label>
          <input
            ref={passRef}
            id="password"
            type="password"
            autoComplete="new-password"
            required={!user}
            minLength={PASS_VALIDATORS.minLength}
            className="input mb-0 peer"
            placeholder="****"
          />
          <p className="mt-2 text-sm text-gray-600">
            {intl.formatMessage({ id: "users.manage.passwordEmailHint" })}
          </p>
          {passwordError && (
            <span className="mt-2 block text-xs text-red-500">
              {intl.formatMessage(
                { id: "validators.passwordRequirements" },
                { minLength: PASS_VALIDATORS.minLength }
              )}
            </span>
          )}
        </div>

        <label className="subtitle mr-5" htmlFor="role">
          {intl.formatMessage({ id: "users.manage.roleLabel" })}
        </label>
        <select
          className="cursor-pointer input w-fit"
          id="role"
          value={selectedRole}
          onChange={(e) => setSelectedRole(e.target.value as UserRole)}
        >
          {canAssignSdiAdmin && (
            <option className="cursor-pointer" value={UserRole.admin}>
              {intl.formatMessage({ id: "misc.roles.admin" })}
            </option>
          )}
          {canAssignSiteAdmin && (
            <option className="cursor-pointer" value={UserRole.site_admin}>
              {intl.formatMessage({ id: "misc.roles.siteAdmin" })}
            </option>
          )}
          <option className="cursor-pointer" value={UserRole.operator}>
            {intl.formatMessage({ id: "misc.roles.operator" })}
          </option>
        </select>

        {selectedRole === UserRole.operator && (
          <div className="mt-4">
            <label className="subtitle block mb-2">
              {intl.formatMessage({ id: "users.manage.assignedSites" })}
            </label>
            <div className="flex flex-col gap-2 max-h-40 overflow-y-auto border border-slate-200 rounded-lg p-3">
              {sites && sites.length > 0 ? sites.map((site) => (
                <label key={site.id} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedSiteIds.includes(site.id)}
                    onChange={() => toggleSite(site.id)}
                  />
                  {site.name}
                </label>
              )) : (
                <span className="text-sm text-gray-500">
                  {intl.formatMessage({ id: "users.manage.noSites" })}
                </span>
              )}
            </div>
            {isSubmitted && selectedSiteIds.length === 0 && (
              <span className="mt-2 block text-xs text-red-500">
                {intl.formatMessage({ id: "users.manage.siteRequired" })}
              </span>
            )}
          </div>
        )}

        <Button
          type="submit"
          theme="primary"
          size="big"
          className="mt-4 !bg-[#E94E1B] hover:!bg-[#d34515] focus-visible:!ring-[#E94E1B]"
          disabled={isSubmitted}
          onClick={(e) => {e.preventDefault(); saveUser()}}>
          {user
            ? intl.formatMessage({ id: "users.manage.saveEdit" })
            : intl.formatMessage({ id: "users.manage.createAccount" })}
        </Button>
        
        {
          user && !user.accountActivateAt &&
          <Button
            size="big"
            theme="black"
            className="mt-3 !bg-[#023865] hover:!bg-[#012b4d] focus-visible:!ring-[#023865]"
            disabled={resetLoading || activateLoading}
            onClick={() => {activateUser(user.email)}}
        >
            {intl.formatMessage({ id: "users.manage.activateAccount" })}
          </Button>
        }

        {
          user && user.id != null &&
          <Button
            size="big"
            theme="black"
            className="mt-3 !bg-[#023865] hover:!bg-[#012b4d] focus-visible:!ring-[#023865]"
            disabled={resetLoading || activateLoading}
            onClick={handleResetPassword}
          >
            {intl.formatMessage({ id: "users.manage.resetPassword" })}
          </Button>
        }

        {
          user && <>          
          <Button
            size="big"
            theme="outline-danger"
            className="mt-3"
            onClick={() => onDelete()}>
            {intl.formatMessage({ id: "users.manage.deleteUser" })}
          </Button></>
        }

        {
          success &&
          <Alert message={success.message} type="success" className="mt-5" autoHidden={false} />
        }
        {
          activateRes &&
          <Alert message={activateRes.message} type="success" className="mt-5" autoHidden={false} />
        }
        {
          activateError &&
          <Alert message={activateError.message} type="error" className="mt-5" autoHidden={false} />
        }
        {
          resetRes &&
          <Alert message={resetRes.message} type="success" className="mt-5" autoHidden={false} />
        }
        {
          resetError &&
          <Alert message={resetError.message} type="error" className="mt-5" autoHidden={false} />
        }
        {
          error &&
          <Alert message={error.message} type="error" className="mt-5" autoHidden={false} />
        }
      </form>
    </>
  )
}
